import { Link, useParams } from "react-router-dom";
import { ExternalLink, ArrowLeft, ArrowRight, Check, BookOpen, Tag } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import { amazonUrl } from "../data/books";
import { canonicalUrl, SITE_URL } from "../data/seo";
import { useBooks } from "../context/BooksContext";
import BookCover from "../components/BookCover";
import BookCard from "../components/BookCard";
import ShareButton from "../components/ShareButton";

export default function BookDetail() {
  const { slug } = useParams();
  const { getBook, books, loading } = useBooks();
  const book = getBook(slug);
  const comingSoon = book && book.status === "coming_soon";

  const seo = (book && book.seo) || {};
  const metaTitle = seo.title
    || (book ? `${book.shortTitle} | Digsby Early Learning` : "Book Not Found | Digsby Early Learning");
  const metaDesc = seo.metaDescription
    || (book
      ? [`${book.shortTitle}: ${book.tagline}`, book.ageRange, "By Dr. Maydine Alcid-Digsby. Available on Amazon."]
          .filter(Boolean).join(" ").replace(/\s+/g, " ").trim()
      : "The book you are looking for could not be found.");

  const bookLd = book && {
    "@context": "https://schema.org",
    "@type": "Book",
    name: book.title,
    author: { "@type": "Person", name: "Dr. Maydine L. Alcid-Digsby", url: `${SITE_URL}/about` },
    publisher: { "@type": "Organization", name: "Digsby Early Learning", url: SITE_URL },
    bookFormat: book.category === "Workbooks"
      ? "https://schema.org/Paperback"
      : "https://schema.org/EBook",
    description: book.description,
    image: book.image || undefined,
    genre: book.category,
    inLanguage: "en",
    audience: { "@type": "Audience", suggestedMinAge: 2, suggestedMaxAge: 7 },
    url: `${SITE_URL}/books/${book.slug}`,
    ...(book.status === "published" && book.amazon
      ? { offers: { "@type": "Offer", availability: "https://schema.org/InStock", url: amazonUrl(book.amazon) } }
      : {}),
  };

  const breadcrumbLd = book && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Shop", item: `${SITE_URL}/shop` },
      { "@type": "ListItem", position: 3, name: book.shortTitle, item: `${SITE_URL}/books/${book.slug}` },
    ],
  };

  usePageMeta({
    title: metaTitle,
    description: metaDesc,
    canonical: canonicalUrl(`/books/${book ? book.slug : slug}`),
    image: book && book.image,
    type: "book",
    jsonLd: book ? [bookLd, breadcrumbLd] : undefined,
  });

  if (!book) {
    if (loading) {
      return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-500">Loading…</div>;
    }
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-heading font-bold text-3xl">Book not found</h1>
        <Link to="/shop" data-testid="back-to-shop" className="mt-6 inline-flex items-center gap-2 text-[#0000FF] font-bold">
          <ArrowLeft size={18} /> Back to Shop
        </Link>
      </div>
    );
  }

  const related = books.filter((b) => b.category === book.category && b.slug !== book.slug).slice(0, 3);

  return (
    <div className="bg-[#FFFBF2]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        <Link
          to="/shop"
          data-testid="detail-back-to-shop"
          className="inline-flex items-center gap-2 font-bold text-slate-600 hover:text-[#0000FF] mb-8"
        >
          <ArrowLeft size={18} /> Back to all books
        </Link>

        <div className="grid gap-12 lg:grid-cols-2 items-start">
          {/* Cover */}
          <div className="lg:sticky lg:top-28">
            <div className="aspect-[3/4] max-w-md mx-auto rounded-[2rem] overflow-hidden border-4 border-black shadow-[10px_10px_0px_#000]">
              <BookCover book={book} />
            </div>
          </div>

          {/* Info */}
          <div>
            <span
              className="inline-block rounded-full px-4 py-1.5 text-sm font-bold mb-4"
              style={{
                backgroundColor: book.category === "Workbooks" ? "#0000FF" : "#00EE00",
                color: book.category === "Workbooks" ? "#FFFFFF" : "#000000",
              }}
            >
              {book.category}
            </span>

            <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight text-black">
              {book.shortTitle}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              {book.ageRange && (
                <span className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-black/10 px-4 py-1.5 text-sm font-bold text-slate-700">
                  <Tag size={15} /> {book.ageRange}
                </span>
              )}
              {book.audience && (
                <span className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-black/10 px-4 py-1.5 text-sm font-bold text-slate-700">
                  <BookOpen size={15} /> {book.audience}
                </span>
              )}
            </div>

            <p className="mt-6 text-lg text-slate-700 leading-relaxed whitespace-pre-line">
              {book.description}
            </p>

            <div className="mt-8">
              <h2 className="font-heading font-bold text-2xl mb-4">What it teaches</h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {book.skills.map((s, i) => (
                  <li key={i} className="flex items-start gap-3 bg-white rounded-2xl border-2 border-black/5 p-4">
                    <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-[#00EE00] flex items-center justify-center">
                      <Check size={16} className="text-black" strokeWidth={3} />
                    </span>
                    <span className="font-medium text-slate-800">{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              {comingSoon ? (
                <>
                  <Link
                    to="/#learning-circle"
                    data-testid="detail-join-waitlist"
                    className="inline-flex items-center justify-center gap-2 min-h-[56px] rounded-full bg-[#FF0000] text-white font-bold text-lg px-8 hover:bg-[#d60000] hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
                  >
                    Join the Learning Circle <ArrowRight size={20} />
                  </Link>
                  <span
                    data-testid="detail-coming-soon"
                    className="inline-flex items-center justify-center gap-2 min-h-[56px] rounded-full bg-[#FBEF14] border-2 border-black text-black font-bold text-lg px-8 cursor-default"
                  >
                    Coming Soon
                  </span>
                </>
              ) : (
                <a
                  href={amazonUrl(book.amazon)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="detail-buy-amazon"
                  className="inline-flex items-center justify-center gap-2 min-h-[56px] rounded-full bg-[#FF0000] text-white font-bold text-lg px-8 hover:bg-[#d60000] hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
                >
                  Buy on Amazon <ExternalLink size={20} />
                </a>
              )}
              {book.amazonKindle && (
                <a
                  href={amazonUrl(book.amazonKindle)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="detail-buy-kindle"
                  className="inline-flex items-center justify-center gap-2 min-h-[56px] rounded-full bg-white border-2 border-black text-black font-bold text-lg px-8 hover:bg-[#FBEF14] hover:-translate-y-1 transition-all duration-200"
                >
                  Kindle Edition <ExternalLink size={18} />
                </a>
              )}
              <ShareButton book={book} variant="button" />
            </div>
            <p className="mt-3 text-sm text-slate-500">
              {comingSoon
                ? "This title isn't available yet, check back soon!"
                : "Opens Amazon in a new tab. Payment, printing, and shipping are handled by Amazon."}
            </p>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-20">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-8">
              More {book.category}
            </h2>
            <div className="grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((b) => (
                <BookCard key={b.slug} book={b} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
